import React, { useCallback, useEffect, useMemo, useState } from "react";
import { smsMessagesApi } from "../api/routes";
import { Button, Card, Input, Select, Toast } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const timeOptions = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last7" },
  { label: "Last 30 days", value: "last30" },
  { label: "Last 90 days", value: "last90" },
];

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getSinceEpochMs = (preset) => {
  const now = new Date();
  if (preset === "today") return startOfDay(now).getTime();
  if (preset === "yesterday") {
    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    return startOfDay(y).getTime();
  }
  if (preset === "last7") return now.getTime() - 7 * 24 * 60 * 60 * 1000;
  if (preset === "last30") return now.getTime() - 30 * 24 * 60 * 60 * 1000;
  if (preset === "last90") return now.getTime() - 90 * 24 * 60 * 60 * 1000;
  return startOfDay(now).getTime();
};

const extractMessageTs = (messageSk) => {
  if (!messageSk) return 0;
  const parts = String(messageSk).split("#");
  const numeric = parts.find((part) => /^\d+$/.test(part));
  if (!numeric) return 0;
  const raw = Number(numeric);
  return raw < 1000000000000 ? raw * 1000 : raw;
};

const formatEpoch = (value) => {
  if (!value && value !== 0) return "";
  const raw = Number(value);
  if (Number.isNaN(raw)) return "";
  const ms = raw < 1000000000000 ? raw * 1000 : raw;
  return new Date(ms).toLocaleString();
};

export default function MessagesPanel() {
  const [panelState, setPanelState] = usePanelCache("smsMessages");
  const [userId, setUserId] = useState(panelState.userId || "");
  const [sincePreset, setSincePreset] = useState(panelState.sincePreset || "today");
  const [messageText, setMessageText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const sinceEpochMs = useMemo(() => getSinceEpochMs(sincePreset), [sincePreset]);

  useEffect(() => {
    setPanelState({ userId, sincePreset });
  }, [userId, sincePreset, setPanelState]);

  const fetchMessages = useCallback(
    async (quiet = false) => {
      if (!userId) return;
      if (!quiet) setLoading(true);
      try {
        const res = await smsMessagesApi.byUserSinceEpoch(userId, sinceEpochMs);
        const list = Array.isArray(res.data) ? res.data : [];
        setPanelState({ results: list, showSearch: false });
      } catch (e) {
        setPanelState({ results: [] });
        setToast({ message: e.message || "Error fetching messages", tone: "error" });
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [userId, sinceEpochMs, setPanelState]
  );

  useEffect(() => {
    if (!userId) return undefined;
    const interval = setInterval(() => fetchMessages(true), 10000);
    return () => clearInterval(interval);
  }, [userId, sinceEpochMs, fetchMessages]);

  const sendMessage = async () => {
    if (!userId || !messageText.trim()) return;
    setSending(true);
    try {
      await smsMessagesApi.add({
        userId,
        text: messageText.trim(),
        direction: "INBOUND",
      });
      setMessageText("");
      fetchMessages(true);
    } catch (e) {
      setToast({ message: e.message || "Error sending message", tone: "error" });
    } finally {
      setSending(false);
    }
  };

  const sorted = useMemo(() => {
    const results = panelState.results || [];
    return [...results].sort((a, b) => extractMessageTs(a.messageSk) - extractMessageTs(b.messageSk));
  }, [panelState.results]);

  return (
    <>
      <Card
        title="Messages"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <Button tone="ghost" onClick={() => setPanelState({ results: [] })}>Clear</Button>
            <Button tone="ghost" onClick={() => fetchMessages()} disabled={!userId} loading={loading}>Refresh</Button>
          </div>
        }
      >
        <Card title="Search">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 10 }}>
            <Input label="User ID" value={userId} onChange={setUserId} />
            <Select label="Since" value={sincePreset} onChange={setSincePreset} options={timeOptions} />
          </div>
          <Button onClick={() => fetchMessages()} disabled={!userId} loading={loading}>Search</Button>
        </Card>

        <div
          style={{
            marginTop: 12,
            borderRadius: 14,
            border: "1px solid rgba(15,23,42,0.08)",
            background: "rgba(15,23,42,0.02)",
            padding: 16,
            maxHeight: 420,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {sorted.length === 0 ? (
            <div style={{ color: "var(--stone-700)" }}>
              No messages yet. Search by user and time window.
            </div>
          ) : (
            sorted.map((message) => {
              const outbound = message.direction === "INBOUND";
              const bubble = {
                alignSelf: outbound ? "flex-end" : "flex-start",
                background: outbound ? "#0c3b2e" : "#e2fbe8",
                color: outbound ? "#fff" : "#0c3b2e",
                padding: "10px 12px",
                borderRadius: 14,
                maxWidth: "70%",
                boxShadow: outbound ? "0 8px 20px rgba(12,59,46,0.18)" : "0 8px 18px rgba(15,23,42,0.08)",
              };
              const ts = extractMessageTs(message.messageSk);
              return (
                <div key={message.messageSk || `${message.userId}-${message.text}`} style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: bubble.alignSelf }}>
                  <div style={bubble}>
                    <div style={{ whiteSpace: "pre-wrap" }}>{message.text}</div>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(15,23,42,0.6)" }}>
                    {message.direction || "UNKNOWN"}{ts ? ` • ${formatEpoch(ts)}` : ""}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "flex-end" }}>
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type a message…"
            rows={2}
            style={{
              flex: 1,
              borderRadius: 12,
              border: "1px solid rgba(15, 23, 42, 0.12)",
              padding: "10px 12px",
              background: "rgba(255,255,255,0.9)",
              fontSize: 14,
              outline: "none",
              resize: "vertical",
            }}
          />
          <Button onClick={sendMessage} disabled={!userId || !messageText.trim()} loading={sending}>Send</Button>
        </div>
      </Card>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
